// Top-bar notification bell — surfaces unread customer replies.
//
// Two scopes, selected by the viewer's role:
//   - PERSONAL (CSR-style users, tenant_users.role='user'): only
//     conversations assigned to me. Fetches /ops/api/conversations
//     ?view=my and counts those with unreadCount > 0.
//   - TEAM-WIDE (tenant admins + platform staff): every open
//     conversation in the active tenant with an unseen customer
//     message, PLUS every escalation that has no owner. Fetches
//     ?unread_only=true so the server pre-filters instead of shipping
//     the whole inbox to the client.
//
// The unowned-escalation half matters as much as the unread half.
// handoff_to_human stamps escalated + high priority with a NULL
// assignee whenever round-robin finds no available team member —
// deliberately, so the escalation does not vanish. Those rows have no
// unread count to sort them by and no assignee to notify, so a bell
// keyed only on "my unread" is precisely where they go missing.
//
// Per-row "mark as read" works only on conversations the viewer is
// assigned to — the /seen endpoint is assignee-gated server-side, so
// admins watching the team queue see the badge until the actual
// assignee opens the chat (intentional: stops admins from accidentally
// clearing tickets they haven't read for their team).
//
// Polling is the realtime mechanism: 12s tick, paused when the tab is
// hidden. Opening the conversation server-side bumps
// last_seen_by_assignee_at, which clears that row from the next poll.
//
// PRESENTATION. The template's header menu (next-shadcn-admin-dashboard:
// dashboard/_components/header/github-repositories-menu.tsx): an icon Button
// that opens a DropdownMenu, its parts drawn with the template menu's
// utilities and the theme tokens, never a colour literal. The rows and both
// mark-as-read controls are menu items, so the arrow keys reach them all.
// Record: docs/testing/ui-migration/030-notification-bell.md.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opsFetch } from "../api";
import { ShellIcon } from "./shell/icons";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface BellItem {
  id: string;
  visitorEmail: string | null;
  visitorName: string | null;
  channel: string;
  status: string;
  unreadCount: number;
  assignedToOpsUserId: string | null;
  assignedToDisplayName: string | null;
  assignedToEmail: string | null;
  lastCustomerMessageAt: string | null;
  escalatedAt: string | null;
  preview: string | null;
}

interface BellListResponse {
  total: number;
  limit: number;
  offset: number;
  items: BellItem[];
}

const POLL_INTERVAL_MS = 12_000;

function relativeShort(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function visitorLabel(item: BellItem): string {
  return item.visitorName || item.visitorEmail || `anon · ${item.id.slice(0, 8)}`;
}

export default function NotificationBell({
  meId,
  teamScope,
}: {
  meId: string;
  // When true the bell shows every unread conversation in the active
  // tenant (admin / platform-staff view). When false it stays scoped
  // to the viewer's own assignments (CSR view).
  teamScope: boolean;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<BellItem[]>([]);
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  // Track per-row mark-as-read requests so the same button can't be
  // double-fired while the network round-trip is in flight. Stored as
  // a Set instead of a single id so other rows stay clickable while
  // one is pending.
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());
  // (An outside click and Escape close the menu: Radix's, not a listener.)

  // An escalation nobody owns needs a human whether or not the customer
  // has written again — handoff_to_human leaves the assignee null when
  // round-robin finds no available team member, and that row is exactly
  // the one that must not be missed.
  const isUnowned = (it: BellItem) => !!it.escalatedAt && !it.assignedToOpsUserId;

  // Personal scope keeps the assignee filter; team scope drops it (the
  // server already restricted to the active tenant). Re-checking
  // `unreadCount > 0` here is not redundant with the server: it drops
  // rows that went stale between the poll and a /seen call from another
  // tab. Unowned escalations bypass that check by design — they have no
  // unread count to qualify on.
  const unread = useMemo(
    () =>
      items.filter((it) =>
        teamScope
          ? it.unreadCount > 0 || isUnowned(it)
          : it.assignedToOpsUserId === meId && it.unreadCount > 0,
      ),
    [items, meId, teamScope],
  );
  // Badge math = count of conversations that need attention, NOT the
  // total message count. Matches Gmail/Slack convention ("3 chats" is
  // more useful than "8 unread messages" at a glance). The per-row
  // numeric pill inside the dropdown still shows the exact message
  // count for each conversation, so the precision isn't lost.
  const totalUnread = unread.length;
  const totalUnreadMessages = unread.reduce((n, it) => n + it.unreadCount, 0);
  // The badge counts CONVERSATIONS NEEDING A LOOK, which is not the same axis
  // as the per-person "N open" on Home — a person can own work with nothing
  // unread. It also is not purely "unread": an escalation nobody owns counts
  // even in silence. The tooltip said "Notifications", which told a reader
  // neither of those things, so the number looked like it ought to reconcile
  // with the open counts and never did. One string, shown to sighted and
  // screen-reader users alike.
  const bellSummary =
    totalUnread === 0
      ? "Nothing waiting — no unread replies or unowned escalations"
      : `${totalUnread} ${totalUnread === 1 ? "conversation needs" : "conversations need"} a look` +
        ` · ${totalUnreadMessages} unread ${totalUnreadMessages === 1 ? "message" : "messages"}` +
        " and any escalation nobody owns";

  const fetchRef = useRef<() => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    if (typeof document === "undefined") return;
    let timer: number | undefined;
    let cancelled = false;

    // Never overlap a poll with itself (see EngageConversations for why).
    let inFlight = false;
    async function fetchOnce() {
      if (inFlight) return;
      inFlight = true;
      try {
        // Team scope: ?unread_only=true returns every conversation in
        // the active tenant with at least one unseen customer message
        // — server-side EXISTS predicate so we don't ship the whole
        // inbox just to filter on the client.
        // Personal scope: ?view=my returns the viewer's assignments
        // regardless of unread state; we filter unread client-side so
        // the bell can also report "you're all caught up" accurately.
        const url = teamScope
          ? "/ops/api/conversations?unread_only=true&limit=200"
          : "/ops/api/conversations?view=my&limit=50";
        const res = await opsFetch<BellListResponse>(url);
        if (!cancelled) setItems(res.items);
      } catch {
        // Fail silently — the bell shouldn't error-out the page.
      } finally {
        inFlight = false;
      }
    }
    fetchRef.current = fetchOnce;

    function start() {
      stop();
      timer = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    }
    function stop() {
      if (timer != null) {
        window.clearInterval(timer);
        timer = undefined;
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        fetchOnce();
        start();
      } else {
        stop();
      }
    }

    if (document.visibilityState === "visible") {
      fetchOnce();
      start();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [teamScope]);

  // Mark every unread row the viewer is allowed to mark. The /seen
  // endpoint is a no-op for non-assignees, so in team scope we only
  // fan out to rows owned by the viewer — anything else would just
  // burn requests for a guaranteed no-op. Personal scope is "all of
  // them" because by definition every row is owned by the viewer.
  const markAllAsRead = useCallback(async () => {
    if (markingAll || unread.length === 0) return;
    const mine = unread.filter((it) => it.assignedToOpsUserId === meId);
    if (mine.length === 0) return;
    setMarkingAll(true);
    try {
      await Promise.all(
        mine.map((it) =>
          opsFetch(`/ops/api/conversations/${it.id}/seen`, { method: "POST" }).catch(() => {
            /* per-item failure is fine; the next poll will reconcile */
          }),
        ),
      );
      await fetchRef.current();
    } finally {
      setMarkingAll(false);
    }
  }, [markingAll, unread, meId]);

  // Per-row mark-as-read. Optimistically clears the row locally so the
  // click feels instant, then refetches to reconcile. Only rendered for
  // rows the viewer actually owns — the server /seen endpoint is a
  // no-op for everyone else, so a button there would just look broken.
  const markOneAsRead = useCallback(
    async (id: string) => {
      if (markingIds.has(id)) return;
      setMarkingIds((s) => new Set(s).add(id));
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, unreadCount: 0 } : it)));
      try {
        await opsFetch(`/ops/api/conversations/${id}/seen`, { method: "POST" });
      } catch {
        /* the refetch below repaints the badge if the request failed */
      } finally {
        await fetchRef.current();
        setMarkingIds((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [markingIds],
  );

  const hasMine = unread.some((it) => it.assignedToOpsUserId === meId);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button size="icon" className="nb-trigger" title={bellSummary} aria-label={bellSummary}>
          <ShellIcon name="bell" size={17} />
          {totalUnread > 0 && (
            <span className="nb-badge">{totalUnread > 99 ? "99+" : totalUnread}</span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[380px] p-0">
        <div className="flex items-center justify-between gap-2 px-1 pt-1">
          <DropdownMenuLabel className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">
              {totalUnread > 0
                ? `${totalUnread} ${totalUnread === 1 ? "conversation" : "conversations"} need${totalUnread === 1 ? "s" : ""} attention`
                : "No new messages"}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {totalUnreadMessages > 0
                ? `${totalUnreadMessages} new customer ${totalUnreadMessages === 1 ? "message" : "messages"} · ${teamScope ? "Team queue" : "Assigned to you"}`
                : teamScope
                  ? "Team queue"
                  : "Assigned to you"}
            </span>
          </DropdownMenuLabel>
          {/* "Mark all as read" only appears when at least one row is
              actually mine — an admin watching the team queue would
              otherwise get a button that is a guaranteed no-op. It keeps
              the menu open, as the button it replaces did. */}
          {hasMine && (
            <DropdownMenuItem
              className="shrink-0 text-xs"
              disabled={markingAll}
              onSelect={(e) => {
                e.preventDefault();
                markAllAsRead();
              }}
            >
              {markingAll ? "Marking…" : "Mark all as read"}
            </DropdownMenuItem>
          )}
        </div>
        <DropdownMenuSeparator />

        {/* WHY THE BADGE WILL NOT MOVE, SAID OUT LOUD.
            Hiding the two mark-as-read controls when the viewer owns none of
            these rows is right — both would be guaranteed no-ops — but it
            left the panel with no controls and no explanation, and reading a
            conversation does not clear it either: /seen only writes for the
            assignee. A supervisor then opens each one, comes back, finds the
            same number, and reports the bell as broken. It was not; it was
            silent.
            An unowned escalation leaves this list by being TAKEN, not by
            being read — nobody has seen it in the sense the badge means. */}
        {unread.length > 0 && !hasMine && (
          <div className="mx-1 mb-1 rounded-md bg-muted px-2 py-1.5 text-xs leading-relaxed text-muted-foreground">
            None of these are assigned to you, so reading them will not clear the
            count — it tracks what the ASSIGNEE has seen. An escalation with no
            owner leaves this list when somebody takes it.
          </div>
        )}

        <div className="max-h-[420px] overflow-y-auto p-1">
          {unread.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm leading-relaxed text-muted-foreground">
              {teamScope
                ? "Team queue is clear. Unread customer replies on any conversation in this workspace will appear here."
                : "You're caught up. New customer replies on your assigned conversations will appear here."}
            </div>
          ) : (
            unread.map((it) => {
              const isMine = it.assignedToOpsUserId === meId;
              // Resolved assignee label — falls back to a generic
              // "someone" only when the row has an assignee id but the
              // JOIN returned no name (deactivated ops user, etc.).
              // The server's unread_only filter admits unassigned rows
              // ONLY when they are escalated, which isUnowned() renders
              // explicitly — so reaching this fallback means an assignee
              // id that no longer resolves to a name.
              const assigneeLabel =
                it.assignedToDisplayName || it.assignedToEmail || "someone";
              const isMarking = markingIds.has(it.id);
              return (
                <div key={it.id} className="flex items-stretch gap-1">
                  <DropdownMenuItem
                    className="flex min-w-0 flex-1 flex-col items-stretch gap-0.5 py-2"
                    onSelect={() => {
                      setOpen(false);
                      navigate(`/conversations/${it.id}`);
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="min-w-0 truncate font-medium">{visitorLabel(it)}</span>
                      {it.unreadCount > 0 && (
                        <span className="rounded-full bg-destructive/10 px-1.5 text-xs font-medium text-destructive tabular-nums dark:bg-destructive/20">
                          {it.unreadCount}
                        </span>
                      )}
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {relativeShort(it.lastCustomerMessageAt)}
                      </span>
                    </div>
                    {/* Assignee line — only useful in team scope; in
                        personal scope every row is "me", so it is noise. */}
                    {teamScope && (
                      <div
                        className={
                          isUnowned(it)
                            ? "text-xs font-medium text-[var(--warn)]"
                            : isMine
                              ? "text-xs font-medium text-foreground"
                              : "text-xs text-muted-foreground"
                        }
                      >
                        {isUnowned(it)
                          ? "Escalated · nobody assigned"
                          : isMine
                            ? "Assigned to you"
                            : `Assigned to ${assigneeLabel}`}
                      </div>
                    )}
                    {it.preview && <div className="line-clamp-2 text-xs text-muted-foreground">{it.preview}</div>}
                  </DropdownMenuItem>
                  {/* Per-row mark-as-read. Only the assignee can clear
                      their own badge (enforced server-side), so the
                      control is hidden rather than shown-and-inert. It
                      keeps the menu open. */}
                  {isMine && (
                    <DropdownMenuItem
                      className="shrink-0 justify-center px-2"
                      disabled={isMarking}
                      onSelect={(e) => {
                        e.preventDefault();
                        markOneAsRead(it.id);
                      }}
                      aria-label="Mark as read"
                      title="Mark as read"
                    >
                      <ShellIcon name="check" size={15} />
                    </DropdownMenuItem>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
